/* eslint-disable  @typescript-eslint/no-explicit-any */
import Vue from "vue";
import Vuex, { StoreOptions, MutationTree, ActionTree, ActionContext } from "vuex";
import utils from "@/utilities/Utils";
import { User } from "@/model/User";
import { AssetSearchParameters } from "@/model/AssetSearchParameters";

Vue.use(Vuex);

//
// -- This allows us to keep globally available data
//    that we can bind to in components...
//

// Define what we're storing
export interface IStoreState {
    signedInUser: User | null;
    pendingApiCall: Promise<any> | null;
    assetSearchParameters: AssetSearchParameters;
    equatorSearchParameters: AssetSearchParameters;
}

const options: StoreOptions<IStoreState> = { 
    state: () => ({
        // shared state
        signedInUser: null as User|null,
        pendingApiCall: null,
        assetSearchParameters: new AssetSearchParameters(),
        equatorSearchParameters: new AssetSearchParameters()
    }),
    getters: {
        // computed state
        isSignedIn: (state: IStoreState) => state.signedInUser !== null && !utils.isEmptyId(state.signedInUser.id),
        signedInUserName: (state: IStoreState) => (state.signedInUser !== null && !utils.isEmptyId(state.signedInUser.id)) ? state.signedInUser.fullname : "(not signed in)"
    },
    mutations: {},  // added below
    actions: {},    // added below
    modules: {}     // guess we don't need this...
};

// couple of shortcuts (and tell subsequent typescript they're *not* undefined)
const mutations = options.mutations as MutationTree<IStoreState>;
const actions = options.actions as ActionTree<IStoreState, IStoreState>;

// used during signing back in
mutations.setPendingApiCall = (state: IStoreState, pendingApiCall: Promise<any>) => {
    state.pendingApiCall = pendingApiCall;
};

actions.setPendingApiCall = (context: ActionContext<IStoreState, IStoreState>, pendingApiCall: Promise<any>) => {
    console.log("STORE - action - setPendingApiCall");
    context.commit("setPendingApiCall", pendingApiCall);
};

// the current user - see @/utilities/Authentication module
mutations.setSignedInUser = (state: IStoreState, user: User) => {
    state.signedInUser = user;
};

actions.loadSignedInUser = (context: ActionContext<IStoreState, IStoreState>) => {
    // try and retrieve from session storage
    const userJson = sessionStorage.getItem("signedInUser");
    if(!userJson) return;
    const user = new User(JSON.parse(userJson as string));
    context.commit("setSignedInUser", user);
};

actions.setSignedInUser = (context: ActionContext<IStoreState, IStoreState>, user: User) => {
    sessionStorage.setItem("signedInUser", JSON.stringify(user));
    context.commit("setSignedInUser", user);
};

mutations.setAssetSearchParameters = (state: IStoreState, params: AssetSearchParameters) => {
    state.assetSearchParameters.pageNumber = params.pageNumber;
    state.assetSearchParameters.searchText = params.searchText;
    state.assetSearchParameters.clientID = params.clientID;
    state.assetSearchParameters.regionIDs = [...params.regionIDs];
    state.assetSearchParameters.categoryIDs = [...params.categoryIDs];
    state.assetSearchParameters.states = [...params.states];
    state.assetSearchParameters.includeArchived = params.includeArchived;
    state.assetSearchParameters.alphaChar = params.alphaChar;
}

actions.setAssetSearchParameters = (context: ActionContext<IStoreState, IStoreState>, params: AssetSearchParameters) => {
    context.commit("setAssetSearchParameters", params)
}

mutations.setEquatorSearchParameters = (state: IStoreState, params: AssetSearchParameters) => {
    state.equatorSearchParameters.pageNumber = params.pageNumber;
    state.equatorSearchParameters.searchText = params.searchText;
    state.equatorSearchParameters.clientID = params.clientID;
    state.equatorSearchParameters.regionIDs = [...params.regionIDs];
    state.equatorSearchParameters.categoryIDs = [...params.categoryIDs];
    state.equatorSearchParameters.states = [...params.states];
    state.equatorSearchParameters.includeArchived = params.includeArchived;
    state.equatorSearchParameters.alphaChar = params.alphaChar;
}

actions.setEquatorSearchParameters = (context: ActionContext<IStoreState, IStoreState>, params: AssetSearchParameters) => {
    context.commit("setEquatorSearchParameters", params)
}

//
// -- the main deal - export the Store
//

export default new Vuex.Store<IStoreState>(options);
